import React, { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { transferSupportTicket } from "../../services/supportService";
import { QUEUE_LABELS } from "../../constants/support";

/** Phase 2 transfer: to another agent and / or a queue, with a required note (audited on the server). */
const TransferDialog: React.FC<{
    ticketId: string | number;
    agents: { id: number; name: string; role: string }[];
    open: boolean;
    onClose: () => void;
    onDone: () => void;
}> = ({ ticketId, agents, open, onClose, onDone }) => {
    const [agent, setAgent] = useState("");
    const [queue, setQueue] = useState("");
    const [note, setNote] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const ready = (!!agent || !!queue) && !!note.trim();

    const submit = async () => {
        setSaving(true);
        try {
            await transferSupportTicket(ticketId, { toAgentId: agent ? Number(agent) : null, toQueue: queue || null, note: note.trim() });
            setAgent(""); setQueue(""); setNote(""); setError(null);
            onDone();
        } catch (e: any) {
            setError(e?.response?.data?.message || "Could not transfer the ticket");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Transfer ticket</DialogTitle>
            <DialogContent>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel id="transfer-agent">To agent (optional)</InputLabel>
                    <Select labelId="transfer-agent" label="To agent (optional)" value={agent} onChange={(e) => setAgent(String(e.target.value))}>
                        <MenuItem value="">—</MenuItem>
                        {agents.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name} — {a.role}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                    <InputLabel id="transfer-queue">To queue (optional)</InputLabel>
                    <Select labelId="transfer-queue" label="To queue (optional)" value={queue} onChange={(e) => setQueue(String(e.target.value))}>
                        <MenuItem value="">—</MenuItem>
                        {Object.entries(QUEUE_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField fullWidth size="small" sx={{ mt: 2 }} label="Note for the next agent" value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 500 }} multiline minRows={2} />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button onClick={submit} variant="contained" disabled={!ready || saving}>Transfer</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransferDialog;
